import type { ContactInfoDto, ContactListDto } from '@features/contacts';
import UseOutreachApi from '@features/Settings/hooks/useOutreachApi.ts';
import { useOutreachSyncApi } from '@features/contacts/hooks/useOutreachSyncApi.ts';
import {
  type OutreachAccount,
  type OutreachProspectDto,
  type OutreachSequenceStep
} from '@types';
import { useSessionStore } from '@features/auth';
import { IntegrationsContactTypes } from '@features/Settings/enums/IntegrationsEnums.ts';
import { useCallback, useEffect, useRef, useState } from 'react';

import { handleError } from '@utils/handleAPIError.ts';
import { writeWarningLog } from '@lib/logger/logWarning.ts';
import { writeErrorLog } from '@lib/logger/logError.ts';
import { writeInfoLog } from '@lib/logger/logInfo.ts';


function convertOutreachSequencesToLists (
  sequences: OutreachSequenceStep[],
  organizationId: string
): ContactListDto[] {
  return sequences.map((sequence) => {
    const { id, attributes, tendrilId, sequenceId } = sequence;
    return {
      id: id.toString(),
      organizationId,
      title: `${sequence.sequenceName} - ${attributes.displayName}`,
      tendrilId,
      sequenceId,
      type: IntegrationsContactTypes.Outreach
    };
  });
}

function convertOutreachProspectToContact (prospects: OutreachProspectDto[]): ContactInfoDto[] {
  return prospects.map((prospect) => {
    return {
      id: prospect.outreachProspectId.toString(),
      contactListId: prospect.tendrilId ?? '',
      name: prospect.name ?? '',
      phone: prospect.phone ?? '',
      jobTitle: prospect.jobTitle ?? '',
      notes: prospect.notes ?? '',
      accountName: prospect.accountName ?? '',
      email: prospect.email ?? '',
    };
  });
}

interface UseOutreachViewModelReturn {
  retrieveOutreachLists: ()=> Promise<ContactListDto[] | undefined>
  retrieveOutreachContacts: (nonce: string, sequenceId: number, sequenceStepId: number, resync: boolean)=> Promise<ContactInfoDto[] | undefined>,
  retrieveOutreachAccounts: () => Promise<string | OutreachAccount[]>
  nonce: string,
}

export function useOutreachViewModel (): UseOutreachViewModelReturn {
  const currentUser = useSessionStore((sessionState) => sessionState.currentUser);
  const organizationId = String(currentUser?.organizationId ?? '').trim();
  const userId = String(currentUser?.userId ?? '').trim();
  const nonceRef = useRef<string>('');
  const [accountsAreLinked, setAccountsAreLinked] = useState(true);

  const { getAllOutreachAccounts }  = UseOutreachApi();
  const { getOutreachSequencesSteps, getOutreachProspects } = useOutreachSyncApi();

  const retrieveOutreachAccounts: () => Promise<string | OutreachAccount[]> = async () => {
    const { data: accountsResponse, error: accountsError } = await getAllOutreachAccounts(userId, organizationId);

    if (accountsResponse?.length === 0) {
      nonceRef.current = '';
      await writeWarningLog(
        'No Outreach linked accounts were found.',
        retrieveOutreachAccounts.name,
        { accountsResponse }
      );
    }

    if (accountsResponse === undefined ) {
      nonceRef.current = '';
      void handleError(
        'Error while getting hubspot accounts.',
        accountsError ?? 'Unknown error',
        retrieveOutreachAccounts.name
      );
      return accountsError?.message ?? 'Error while getting hubspot accounts.';
    }

    nonceRef.current = accountsResponse[0]?.nonce ?? '';

    return accountsResponse;
  };

  const retrieveOutreachLists = useCallback(async (): Promise<ContactListDto[] | undefined> => {
    if (!accountsAreLinked) {
      return undefined;
    }

    const { data: sequenceSteps, error: sequencesError } = await getOutreachSequencesSteps(userId, organizationId);
    if (sequenceSteps === undefined || sequenceSteps.length === 0) {
      await writeErrorLog(sequencesError?.message ?? 'Error while getting outreach sequence steps.',
        'useOutreachViewModel.retrieveOutreachLists',
        { organizationId, userId }
      );
      return undefined;
    }

    const convertedLists = convertOutreachSequencesToLists(sequenceSteps, organizationId);
    await writeInfoLog('Outreach lists retrieved successfully', 'useOutreachViewModel.retrieveOutreachLists', { count: convertedLists.length });
    return convertedLists;
  }, [getAllOutreachAccounts, getOutreachSequencesSteps, organizationId, userId]);

  const retrieveOutreachContacts = useCallback(async (nonce: string, sequenceId: number, sequenceStepId: number, resync: boolean): Promise<ContactInfoDto[] | undefined> => {
    if (!accountsAreLinked) {
      return undefined;
    }

    const { data } = await getAllOutreachAccounts(userId, organizationId);

    let ORnonce;
    if (data !== undefined) {
      ORnonce = data[0].nonce ?? '';
    }

    const { data: prospectsData, error: prospectsError } = await getOutreachProspects(String(ORnonce), sequenceId, sequenceStepId, resync);

    let prospects: OutreachProspectDto[] | undefined;
    
    if (prospectsData === undefined) {
      prospects = undefined;
      return prospects;
    }
    prospects = (prospectsData as unknown as { prospects: OutreachProspectDto[] }).prospects;


    if (prospects === undefined || !Array.isArray(prospects)) {
      await writeErrorLog(prospectsError?.message ?? 'Error while getting outreach prospects.',
        'useOutreachViewModel.retrieveOutreachContacts',
        { nonce, sequenceId, sequenceStepId }
      );
      return undefined;
    }

    if (prospects.length === 0) {
      await writeInfoLog('No prospects found for this sequence step.',
        'useOutreachViewModel.retrieveOutreachContacts',
        { nonce, sequenceId, sequenceStepId }
      );
      return [];
    }

    const convertedContacts = convertOutreachProspectToContact(prospects);
    await writeInfoLog(
      'Outreach prospects retrieved successfully', 
      'useOutreachViewModel.retrieveOutreachContacts', 
      { count: convertedContacts.length }
    );
    return convertedContacts;
  }, [getOutreachProspects]);

  useEffect(() => {
    setAccountsAreLinked(nonceRef.current === '');
  }, []);

  return {
    retrieveOutreachLists,
    retrieveOutreachContacts,
    retrieveOutreachAccounts,
    nonce: nonceRef.current
  };
}